/**
 * Deleting inactive accounts (privacy policy, section 6). Every place that
 * shows or applies the rule reads these numbers.
 *
 * The rule: an account with no activity for INACTIVE_MONTHS is deleted, but
 * never without an e-mail first and never sooner than NOTICE_DAYS after it.
 * "Activity" is the latest of sign-up, sign-in, a CV or profile edit and a
 * logged user action (server/utils/accountRetention.ts).
 */
export const ACCOUNT_RETENTION = {
  inactiveMonths: 12,
  /** Days between the notice and the earliest possible deletion. */
  noticeDays: 30,
  /** Notices sent per day, so a list unused since 2025 does not hurt deliverability. */
  noticesPerDay: 100,
} as const

/**
 * The first notice round, sent with the privacy policy change of 2026-10-10
 * (newsletter_campaign_sends.campaign). A later round needs a new id: an
 * account is only deleted when its notice came after its last activity.
 */
export const RETENTION_NOTICE_CAMPAIGN = 'retention-notice-2026-10'

/** The first round goes to accounts created before this date. */
export const RETENTION_NOTICE_REGISTERED_BEFORE = '2026-09-01'

const DAY_MS = 24 * 60 * 60 * 1000

function addMonths(date: Date, months: number): Date {
  const result = new Date(date.getTime())
  const day = result.getUTCDate()
  result.setUTCDate(1)
  result.setUTCMonth(result.getUTCMonth() + months)
  // 31 March + 1 month is 30 April, not 1 May.
  const lastDay = new Date(Date.UTC(result.getUTCFullYear(), result.getUTCMonth() + 1, 0)).getUTCDate()
  result.setUTCDate(Math.min(day, lastDay))
  return result
}

/** Midnight UTC of the day after `date`: deletion never happens on the date named, only after it. */
function endOfDay(date: Date): Date {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()) + DAY_MS)
}

export interface DeletionDateInput {
  lastActiveAt: Date
  noticeSentAt: Date
  /** Paid access still running: never deleted before it ends. */
  premiumUntil?: Date | null
}

/**
 * The day an account may be deleted: 12 months after its last activity, at
 * least 30 days after the notice and not before paid access ends. The
 * e-mail names this day; deletion is allowed from the day after.
 */
export function accountDeletionDate({ lastActiveAt, noticeSentAt, premiumUntil }: DeletionDateInput): Date {
  const candidates = [
    addMonths(lastActiveAt, ACCOUNT_RETENTION.inactiveMonths),
    new Date(noticeSentAt.getTime() + ACCOUNT_RETENTION.noticeDays * DAY_MS),
  ]
  if (premiumUntil) candidates.push(premiumUntil)
  const latest = new Date(Math.max(...candidates.map(date => date.getTime())))
  return new Date(Date.UTC(latest.getUTCFullYear(), latest.getUTCMonth(), latest.getUTCDate()))
}

/**
 * Whether an account may be deleted now. Only when the notice came after the
 * last activity (anyone who came back after it needs a new notice) and the
 * whole day named in it has passed.
 */
export function isDueForDeletion(input: DeletionDateInput, now: Date = new Date()): boolean {
  if (input.noticeSentAt.getTime() <= input.lastActiveAt.getTime()) return false
  if (input.premiumUntil && input.premiumUntil.getTime() > now.getTime()) return false
  return now.getTime() >= endOfDay(accountDeletionDate(input)).getTime()
}
